/**
 * Finance Workbench — additive Erweiterung des Rechnungslegungs-Assistenten
 * (Phase F, 02.10.2026 Nachtlauf): zeigt die vereinheitlichte Review Queue
 * (Konflikte/fehlende Nachweise/fehlende Opening-Closing-States/unbekannte
 * Discovery-Dokumente aus Phase B-G) direkt unterhalb der bestehenden
 * Vollständigkeits-Checkliste an.
 *
 * WICHTIG: ruft AUSSCHLIESSLICH die echte Route
 * `/api/buchhaltung/finance-workbench/{klient_id}/review-queue` auf und
 * zeigt deren Response direkt an — KEINE lokale Parallelberechnung, kein
 * Mock. Hinter `FINANCE_WORKBENCH_EXTENSIONS_ENABLED` (Default OFF): die
 * Route liefert dann 403 und diese Komponente rendert nichts — bestehendes
 * Verhalten der Rechnungslegung bleibt dadurch unverändert.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, FileQuestion, Gauge, Loader2, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ITEM_TYPEN = {
  konflikt: { label: 'Konflikt', icon: ShieldAlert, klasse: 'bg-red-100 text-red-700 border-red-200' },
  fehlender_nachweis: { label: 'Nachweis fehlt', icon: AlertTriangle, klasse: 'bg-amber-100 text-amber-700 border-amber-200' },
  fehlender_zustand: { label: 'Stand fehlt', icon: Gauge, klasse: 'bg-amber-100 text-amber-700 border-amber-200' },
  unbekanntes_dokument: { label: 'Unbekanntes Dokument', icon: FileQuestion, klasse: 'bg-blue-100 text-blue-700 border-blue-200' },
};

export function FinanceWorkbenchErweiterung({ selectedKlient, von, bis }) {
  const [queue, setQueue] = useState(null);     // null = noch nicht geladen/nicht verfügbar
  const [loading, setLoading] = useState(false);
  const [verfuegbar, setVerfuegbar] = useState(true); // false bei 403 (Flag OFF) -> Komponente verschwindet

  const token = localStorage.getItem('olympus_token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    if (!selectedKlient || !von || !bis) { setQueue(null); return; }
    let abgebrochen = false;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/buchhaltung/finance-workbench/${selectedKlient}/review-queue?von=${von}&bis=${bis}`,
      { headers: authHeaders })
      .then(async (res) => {
        if (abgebrochen) return;
        if (res.status === 403) { setVerfuegbar(false); setQueue(null); return; }
        if (!res.ok) { setQueue(null); return; }
        const data = await res.json();
        setVerfuegbar(true);
        setQueue(data);
      })
      .catch(() => { if (!abgebrochen) setQueue(null); })
      .finally(() => { if (!abgebrochen) setLoading(false); });
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKlient, von, bis]);

  // Flag OFF (403) oder kein Klient/Zeitraum -> bestehendes Verhalten der
  // Rechnungslegung bleibt unverändert, nichts wird angezeigt.
  if (!verfuegbar || (!loading && !queue)) return null;

  return (
    <Card className="border-slate-200" data-testid="finance-workbench-erweiterung">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Gauge className="w-4 h-4 text-slate-500" />
          Vermögenswerte-Review (Erweiterung)
        </CardTitle>
        <CardDescription>
          Depot, Lebensversicherung, Immobilie u.a. — Opening-/Closing-Stand, Nachweise, Discovery
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && (
          <p className="text-sm text-slate-400 flex items-center gap-2" data-testid="finance-workbench-erweiterung-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
          </p>
        )}
        {!loading && queue && queue.anzahl === 0 && (
          <p className="text-sm text-slate-400">Keine offenen Punkte bei den Vermögenswerten.</p>
        )}
        {!loading && queue && queue.anzahl > 0 && (
          <div className="space-y-2">
            {queue.items.map((item, i) => {
              const typ = ITEM_TYPEN[item.typ] || { label: item.typ, icon: FileQuestion, klasse: 'bg-slate-100 text-slate-700 border-slate-200' };
              const Icon = typ.icon;
              return (
                <div key={i} className="flex items-start gap-2 py-2 border-b border-slate-100 last:border-0"
                  data-testid={`finance-workbench-item-${i}`}>
                  <Badge variant="outline" className={`shrink-0 ${typ.klasse}`}>
                    <Icon className="w-3 h-3 mr-1 inline" />{typ.label}
                  </Badge>
                  <p className="text-sm text-slate-700">{item.beschreibung}</p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
